import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import { AnimatePresence, motion } from 'framer-motion';
import { ScrollText, Reveal } from '../../../components/motion';
import { buildFaqJsonLd } from '../../../lib/seo';
import courierImg from '../../../assets/images/sahari-courier.webp';
import { HOME_FAQS } from './homeFaq';
import Eyebrow from '../../../components/ui/Eyebrow';
import styles from './HomeFaqSection.module.css';

const pad = (n: number) => String(n).padStart(2, '0');

/**
 * Home FAQ — questions numbered with big outlined numbers; the open one gets a
 * red bar and red text. Beside it, the Sahari courier photo with a "still have
 * a question?" box that stays in view while scrolling.
 */
const HomeFaqSection: React.FC = () => {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <section className={styles.faq}>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(buildFaqJsonLd(HOME_FAQS)) }}
      />
      <Container fluid="xl" className={styles.inner}>
        <div className={styles.layout}>
          <div className={styles.main}>
            <header className={styles.head}>
              <Eyebrow>Questions</Eyebrow>
              <ScrollText as="h2" className={styles.title}>
                Frequently Asked Questions
              </ScrollText>
              <Reveal as="p" className={styles.subtitle} delay={0.05}>
                Sending cargo from Saudi Arabia — what people ask us most
              </Reveal>
            </header>

            <Reveal className={styles.list} delay={0.1}>
              {HOME_FAQS.map(({ q, a }, i) => {
                const isOpen = open === i;
                const panelId = `home-faq-${i}`;
                return (
                  <div key={q} className={`${styles.item} ${isOpen ? styles.itemOpen : ''}`}>
                    <span className={styles.num} aria-hidden="true">
                      {pad(i + 1)}
                    </span>
                    <h3 className={styles.qHeading}>
                      <button
                        type="button"
                        className={styles.question}
                        aria-expanded={isOpen}
                        aria-controls={panelId}
                        onClick={() => setOpen(isOpen ? null : i)}
                      >
                        {q}
                      </button>
                    </h3>
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          id={panelId}
                          key="panel"
                          className={styles.panel}
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.25, ease: 'easeOut' }}
                        >
                          <p className={styles.answer}>{a}</p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </Reveal>
          </div>

          <Reveal className={styles.aside} delay={0.1}>
            <div className={styles.photo}>
              <img src={courierImg} alt="Sahari Cargo courier with a Sahari box" loading="lazy" />
              <div className={styles.help}>
                <span>
                  <b>Still have a question?</b>
                  <small>Our team answers the same day</small>
                </span>
                <Link to="/contact" className={styles.helpBtn}>
                  Contact us
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </Container>
    </section>
  );
};

export default HomeFaqSection;
